import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Flex,
  Text,
} from '@chakra-ui/react'
import { useNavigationStore } from '@hb/react'
import { useCallback, useRef, useState } from 'react'
import { NavigateOptions, To, useNavigate } from 'react-router'
import { ActionButton, SolidActionButton } from '../Buttons/ActionButton'
import { CollapseError } from '../CollapseError'

export const NavigationGuardModal = () => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const rrNavigate = useNavigate()
  const [isSaving, setIsSaving] = useState(false)

  const pendingNavigation = useNavigationStore(s => s.pendingNavigation)
  const setPendingNavigation = useNavigationStore(s => s.setPendingNavigation)
  const setUnsavedFormChange = useNavigationStore(s => s.setUnsavedFormChange)
  const onSave = useNavigationStore(s => s.unsavedFormChange?.onSave)
  const [saveError, setSaveError] = useState<string | null>(null)

  const doNavigate = useCallback(
    (nav: typeof pendingNavigation) => {
      if (!nav) return
      const { to, options } = nav
      setUnsavedFormChange(null)
      setPendingNavigation(null)
      if (typeof to === 'number') {
        rrNavigate(to)
      } else {
        rrNavigate(to as To, options as NavigateOptions | undefined)
      }
    },
    [rrNavigate, setUnsavedFormChange, setPendingNavigation],
  )

  const handleConfirm = useCallback(() => {
    setSaveError(null)
    doNavigate(pendingNavigation)
  }, [pendingNavigation, doNavigate])

  const handleSaveAndNavigate = useCallback(async () => {
    setSaveError(null)
    if (!pendingNavigation || !onSave) return
    setIsSaving(true)
    try {
      await onSave()
      doNavigate(pendingNavigation)
    } catch (e: any) {
      setSaveError(e?.message || 'An error occurred while saving the form')
    } finally {
      setIsSaving(false)
    }
  }, [pendingNavigation, onSave, doNavigate, setSaveError])

  const handleCancel = useCallback(() => {
    setSaveError(null)
    setPendingNavigation(null)
  }, [setPendingNavigation])

  return (
    <AlertDialog
      isCentered
      isOpen={!!pendingNavigation}
      leastDestructiveRef={cancelRef}
      onClose={handleCancel}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontFamily="Hero-New" pb={2} fontSize="lg" fontWeight={500}>
            Unsaved Changes
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              You have unsaved changes in {pendingNavigation?.formName}. Are you sure you want to
              leave? Your changes will be lost.
            </Text>
            {<CollapseError error={saveError} style={{ marginTop: '10px' }} />}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Flex gap={2} justify="flex-end" w="100%">
              <ActionButton size="sm" px={5} ref={cancelRef} onClick={handleCancel}>
                Stay
              </ActionButton>
              <SolidActionButton isLoading={isSaving} onClick={handleSaveAndNavigate} size="sm">
                Save & Continue
              </SolidActionButton>
              <SolidActionButton
                colorScheme="red"
                _dark={{ bg: 'red.600' }}
                isDisabled={isSaving}
                onClick={handleConfirm}
                size="sm">
                Discard Changes
              </SolidActionButton>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
